import { LabelWithHint } from "../../components/ui/Tooltip";
import { useSession } from "../auth/useSession";
import {
	isColorValid,
	isHardToRead,
	STARTING_COLORS,
	type Appearance,
	type AppearanceKey,
} from "./appearance";
import { AppearancePreview } from "./AppearancePreview";
import { OptionalColorField } from "./OptionalColorField";

const APPEARANCE_HELP =
	"Colours for this campaign's progress bar, wherever it shows (cart, homepage, product and collection pages), and for its gift popup. A popup colour left to follow your theme matches the progress bar. Anything else left to follow your theme uses the colours from the AOVtools Content app embed, or failing that your theme's own.";

type Group = {
	title: string;
	fields: readonly { key: AppearanceKey; label: string }[];
	/** The chosen pair to warn about when it cannot be read. */
	pair?: { background: AppearanceKey; text: AppearanceKey };
	giftPopupOnly?: boolean;
};

const GROUPS: readonly Group[] = [
	{
		title: "Progress bar",
		fields: [
			{ key: "widgetBackground", label: "Background" },
			{ key: "widgetText", label: "Text" },
		],
		pair: { background: "widgetBackground", text: "widgetText" },
	},
	{
		title: "Gift popup",
		giftPopupOnly: true,
		fields: [
			{ key: "popupBackground", label: "Background" },
			{ key: "popupText", label: "Text" },
		],
		pair: { background: "popupBackground", text: "popupText" },
	},
	{
		title: "Accent",
		fields: [{ key: "accentColor", label: "Buttons and progress fill" }],
	},
];

/** A colour half typed into the hex box previews as nothing chosen until it is whole. */
const previewable = (appearance: Appearance): Appearance => {
	const pick = (key: AppearanceKey) => (isColorValid(appearance[key]) ? appearance[key] : null);
	return {
		accentColor: pick("accentColor"),
		widgetBackground: pick("widgetBackground"),
		widgetText: pick("widgetText"),
		popupBackground: pick("popupBackground"),
		popupText: pick("popupText"),
	};
};

type AppearanceFieldProps = {
	value: Appearance;
	onChange: (value: Appearance) => void;
	/** Only tiered gift campaigns open the gift popup. */
	showGiftPopup: boolean;
};

/** Part of the campaign form: the page's Save sends these with everything else. */
export const AppearanceField = ({ value, onChange, showGiftPopup }: AppearanceFieldProps) => {
	const session = useSession();
	const currency = session.data?.tenant.currencyCode ?? "USD";
	const shown = previewable(value);

	return (
		<section className="space-y-4 rounded-lg border border-line bg-surface p-4">
			<LabelWithHint label="Colours">{APPEARANCE_HELP}</LabelWithHint>

			<div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
				<div className="space-y-5">
					{GROUPS.filter((group) => showGiftPopup || !group.giftPopupOnly).map((group) => (
						<fieldset key={group.title} className="space-y-3">
							<legend className="mb-2 text-xs font-medium tracking-wide text-ink-muted uppercase">
								{group.title}
							</legend>
							{group.fields.map(({ key, label }) => (
								<OptionalColorField
									key={key}
									label={label}
									name={`${group.title} ${label}`}
									value={value[key]}
									startingColor={STARTING_COLORS[key]}
									onChange={(next) => onChange({ ...value, [key]: next })}
								/>
							))}
							{group.pair && isHardToRead(shown[group.pair.background], shown[group.pair.text]) ? (
								<p className="text-xs text-ink-muted">
									These colours are close together, so shoppers may struggle to read the text. The
									storefront uses them exactly as chosen.
								</p>
							) : null}
						</fieldset>
					))}
				</div>

				<AppearancePreview appearance={shown} currency={currency} showGiftPopup={showGiftPopup} />
			</div>
		</section>
	);
};
